import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { AppointmentInfo } from '../../ui-module/appointment-info.js';
import type { AppointmentInfo as AppointmentInfoData } from '../../token-module/appointment-info.interface.js';

const SPINNER_TRANSITION = {
  repeat: Infinity,
  ease: 'linear' as const,
  duration: 0.9,
};

interface LoadingScreenProps {
  readonly appointmentInfo?: AppointmentInfoData;
}

/**
 * Loading-Screen: zentrierte Termininformation und 40-px-Ring-Spinner.
 *
 * Spinner via framer-motion (`animate={{ rotate: 360 }}`) — die globale
 * `prefers-reduced-motion`-Regel deaktiviert die Rotation für Nutzer:innen
 * mit entsprechender Systemeinstellung.
 *
 * A11y: `role="status"` + `aria-live="polite"` signalisiert den Statuswechsel.
 */
export function LoadingScreen(props: LoadingScreenProps = {}): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col items-center gap-8">
      <AppointmentInfo
        headlineKey="loading.headline"
        {...(props.appointmentInfo ? { info: props.appointmentInfo } : {})}
      />
      <div
        role="status"
        aria-live="polite"
        aria-label={t('loading.ariaLabel')}
        className="flex flex-col items-center gap-3"
      >
        <motion.span
          aria-hidden="true"
          className="block h-10 w-10 rounded-full border-[3px] border-brand/20 border-t-brand"
          animate={{ rotate: 360 }}
          transition={SPINNER_TRANSITION}
        />
        <span className="text-sm text-muted-foreground">{t('loading.message')}</span>
      </div>
    </div>
  );
}
